/* eslint-disable react/display-name */
import React from 'react'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { numberWithComma } from 'helpers/format'
import { Col, Image, Popover, Row, Space } from 'antd'
import { appConfig } from 'config'
import { TableListPage } from 'Components/common/Table'
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { ImageFallback } from 'helpers/constanst'
import { PurchaseDataInfo, PurchaseProduct } from 'Store/purchase/types'
import { GrnItemInfo } from 'Store/grn/types'
import ProductUomMultiplier from 'Pages/Product/components/ProductUomMultiplier'
import ProductUomSystemInfo from 'Pages/Product/components/ProductUomSystemInfo'

type Props = {
  products: GrnItemInfo[]
  onClickEdit?: (products: GrnItemInfo) => void
  originalAmount: number
}

const GrnItemProducts: React.FunctionComponent<Props> = props => {
  const { products, originalAmount } = props

  const renderInfo = (val: any) => {
    return <RowTile>{val}</RowTile>
  }

  const renderImage = (purchase_info: PurchaseProduct) => (
    <ThumbImage src={appConfig.assetCdnEndpoint + purchase_info.product_photo} fallback={ImageFallback} />
  )

  const renderProductName = (value: PurchaseProduct) => {
    const isSameTitle = value.product_name == value.product_option_name
    return (
      <>
        <RowTile>{value.product_name}</RowTile>
        {!isSameTitle && <ProductDesc>{value.product_option_name}</ProductDesc>}
      </>
    )
  }

  const renderQuantity = (item: number) => {
    return <RemainProduct>{numberWithComma(item)}</RemainProduct>
  }

  const renderPrice = (item: PurchaseProduct) => {
    return <TextRight>{numberWithComma(item.price_per_unit, true)}</TextRight>
  }

  const renderTotalPrice = (item: PurchaseProduct, record: GrnItemInfo) => {
    return (
      <TextRight>
        {numberWithComma(item.price_per_unit * (record.original_received_amount || 0), true)}
      </TextRight>
    )
  }

  const renderTotalPriceAndHiddenCost = (item: PurchaseProduct, record: GrnItemInfo) => {
    return (
      <TextRight>
        {numberWithComma(
          item.price_per_unit * (record.original_received_amount || 0) + (record.hidden_cost || 0),
          true,
        )}
      </TextRight>
    )
  }

  const renderTooltip = (label: any, title: any) => {
    return (
      <Popover content={title} trigger="hover" placement="bottom" title={''}>
        {label} <InfoCircleOutlined style={{ cursor: 'pointer' }} />
      </Popover>
    )
  }

  const renderProductUom = (purchase_info: PurchaseDataInfo, record: GrnItemInfo) => {
    console.log('record', record)
    const showInfo = record.multiplier > 1

    return (
      <span>
        <ProductUomSystemInfo
          multiplier={record.multiplier}
          quantity={record.amount}
          uom={record.product_uom}
          quantitySystem={record.amount_system}
          uomSystem={record.product_uom_system}
          showQuantity={false}
        />
      </span>
    )
  }

  const columns = [
    {
      title: <ColTile>ลำดับ</ColTile>,
      dataIndex: 'no',
      key: 'no',
      render: (value: any, record: any, index: number) => {
        return renderInfo(index + 1)
      },
      width: '50px',
    },
    {
      title: <ColTile>รูปภาพ</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'product_photo',
      render: renderImage,
      width: '100px',
    },
    {
      title: <ColTile>รหัสสินค้า</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'product_sku',
      width: '100px',
      render: (value: PurchaseProduct) => renderInfo(value.product_sku || '-'),
    },
    {
      title: <ColTile>ยี่ห้อ</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'product_manufacture',
      width: '100px',
      render: (value: PurchaseProduct) => renderInfo(value.product_manufacture || '-'),
    },
    {
      title: <ColTile>รายการ</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'product_name',
      render: renderProductName,
      width: '100px',
    },
    {
      title: <ColTile>หน่วย</ColTile>,
      dataIndex: 'purchasing_item',
      key: 'product_uom',
      width: '100px',
      render: renderProductUom,
    },
    // {
    //   title: <ColTile>จำนวนสั่งซื้อ</ColTile>,
    //   dataIndex: 'purchasing_item',
    //   key: 'amount',
    //   width: '100px',
    //   render: (value: PurchaseProduct) => renderInfo(numberWithComma(value.amount || 0)),
    // },
    {
      title: <ColTile>รับเข้า</ColTile>,
      dataIndex: 'original_received_amount',
      key: 'original_received_amount',
      width: '100px',
      //render: renderQuantity,
      render: (val: number) => <RemainProduct>{numberWithComma(val)}</RemainProduct>,
    },
    {
      title: <ColTile>ลดหนี้</ColTile>,
      dataIndex: 'original_received_amount',
      key: 'original_received_amount',
      width: '100px',
      render: (val: number, record: GrnItemInfo) =>
        numberWithComma((record.original_received_amount || 0) - (record.received_amount || 0)),
    },
    {
      title: <ColTile>คงเหลือ</ColTile>,
      dataIndex: 'received_amount',
      key: 'received_amount',
      width: '100px',
      render: (value: number) => renderInfo(numberWithComma(value || 0)),
    },
    {
      title: (
        <ColTile>
          <TextRight>ราคาต่อหน่วย</TextRight>
        </ColTile>
      ),
      dataIndex: 'purchasing_item',
      key: 'price_per_unit',
      width: '100px',
      render: renderPrice,
    },
    {
      title: (
        <ColTile>
          <TextRight>ราคาสินค้ารวม</TextRight>
        </ColTile>
      ),
      dataIndex: 'purchasing_item',
      key: 'purchasing_item',
      width: '100px',
      render: renderTotalPrice,
    },
    {
      title: <TextRight>ต้นทุนแฝง</TextRight>,
      dataIndex: 'hidden_cost',
      key: 'hidden_cost',
      width: '100px',
      render: (value: number) => <TextRight>{numberWithComma(value || 0, true)}</TextRight>,
    },
    {
      title: (
        <ColTile>
          <TextRight>ราคารวม</TextRight>
        </ColTile>
      ),
      dataIndex: 'purchasing_item',
      key: 'purchasing_item',
      width: '100px',
      render: renderTotalPriceAndHiddenCost,
    },
  ]

  const renderHeader = () => {
    return (
      <Row justify="space-between" gutter={[32, 32]} align="middle">
        <Col>
          <BoxTitle>รายการรับเข้าคลัง</BoxTitle>
        </Col>
      </Row>
    )
  }

  return (
    <BoxWrapper>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {renderHeader()}
          <TableListPage
            rowKey="option_id"
            tableName="product-purchase"
            columns={columns}
            datas={products}
            size={'small'}
            scrollX={800}
            bordered={false}
          />
        </Col>
      </Row>
      {/* <Row justify="space-between" gutter={[8, 8]}>
        <Col></Col>
        <Col>
          <SummaryPrice>
            <Space>
              ราคาสินค้ารวม
              <span> {numberWithComma(countTotalPrice(), true)} </span> บาท
            </Space>
          </SummaryPrice>
        </Col>
      </Row>
      <Row justify="space-between" gutter={[8, 8]}>
        <Col></Col>
        <Col>
          <SummaryPrice>
            <Space>
              ต้นทุนแฝงรวม
              <span> {numberWithComma(countTotalHiddenCost(), true)} </span> บาท
            </Space>
          </SummaryPrice>
        </Col>
      </Row> */}
      <Row justify="space-between" gutter={[8, 8]}>
        <Col></Col>
        <Col>
          <SummaryPrice>
            <Space>
              จำนวนเงินรวมทั้งหมด
              <Price> {numberWithComma(originalAmount, true)} </Price> บาท
            </Space>
          </SummaryPrice>
        </Col>
      </Row>
    </BoxWrapper>
  )
}

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.primary};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const ThumbImage = styled(Image)`
  border-radius: 5px;
  width: 70px;
  height: 70px;
  object-fit: cover;
`

const ColTile = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const RowTile = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const ProductDesc = styled(RowTile)`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body5};
`

const TextRight = styled.div`
  text-align: right;
`

const RemainProduct = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
`

const SummaryPrice = styled.div`
  text-align: right;
`

const Price = styled.div`
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.regular};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.title2};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

export default GrnItemProducts
